/**
 * A filled seat, shown as one row.
 *
 * By September most of a year's roles are hired, and before this the person and
 * the requisition were two rows: the seat on the timeline and the colleague in
 * the current team. Here they are one. The row keeps everything the Hiring
 * Database knows — when the search opened, when the offer was signed, whether
 * this was a new seat or a backfill — and gains who filled it and what they do
 * now, so "was this person part of this year's plan, or were they already here?"
 * is answerable from the row itself.
 *
 * A HIRE WHO HAS NOT STARTED still shows here in full. What they do NOT do is
 * join the current team: the team is who is at work today, and a signed contract
 * is not a colleague until their first day.
 */
import type { BoardRole, HdbRole, HireState } from '../../../types/taDemand';
import { isDraft } from '../../../types/taDemand';
import { ReasonChip } from './demandUi';

/** `2026-04-15` → `15.04.` — the board is German-facing and the year is the board's. */
const shortDay = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return m && d ? `${d}.${m}.` : '';
};

/* Keyed on HdbRole, not BoardRole: a planner draft has nobody hired into it yet
   and `isDraft` returns before any of these labels is reached. */
const STATE_CHIP: Partial<
  Record<HireState, { cls: string; label: (r: HdbRole) => string; help: string }>
> = {
  landed: {
    cls: 'bg-emerald-50 text-emerald-700',
    label: (r) => `started ${shortDay(r.hire?.startDate)}`,
    help: 'Hired through this plan and already at work. Shown here rather than in the current team, so the seat is counted once.',
  },
  incoming: {
    cls: 'bg-sky-50 text-sky-700',
    label: (r) => (r.hire?.startDate ? `starts ${shortDay(r.hire.startDate)}` : 'signed'),
    help: 'Signed but not started. Not part of the current team until their first day.',
  },
  undated: {
    cls: 'bg-amber-50 text-amber-700',
    label: () => 'no start date',
    help: 'Hired, but the Hiring Database has no actual start date — so the board cannot tell whether they are here yet.',
  },
  unmatched: {
    cls: 'bg-gray-100 text-gray-600',
    label: () => 'no record',
    help: 'This start date has passed but nobody by that name is in the people database. They may have left, or the name may be spelled differently.',
  },
  ambiguous: {
    cls: 'bg-amber-50 text-amber-700',
    label: () => 'which one?',
    help: 'Two colleagues share this name. Nothing is merged until somebody says which — a wrong merge would hide a real person.',
  },
  unlinked: {
    cls: 'bg-gray-100 text-gray-500',
    label: () => 'not matched',
    help: 'The current-team list could not be loaded, so this hire could not be matched to a colleague. Not a claim that they have left.',
  },
  superseded: {
    cls: 'bg-gray-100 text-gray-500',
    label: () => 'earlier role',
    help: 'This person has since been hired onto a later role. Their seat is that one; this row is the history of how they arrived.',
  },
};

/** The state of a filled seat. Nothing for a role nobody has been hired into. */
export function HireStateChip({ role }: { role: BoardRole }) {
  if (isDraft(role)) return null;
  const spec = role.hireState ? STATE_CHIP[role.hireState] : undefined;
  if (!spec) return null;
  return (
    <span
      title={spec.help}
      className={`shrink-0 cursor-help rounded-sm px-1.5 text-[11px] font-medium leading-4 ${spec.cls}`}
    >
      {spec.label(role)}
    </span>
  );
}

/**
 * Track 4 — WHO. The colleague once there is one, the reason for hiring until
 * then. The same column answers the same question in both cases, which is why
 * the person does not get a column of their own: on a board that is mostly
 * filled, a seventh track would be empty for the rows still being worked on.
 */
export function HireWho({ role }: { role: BoardRole }) {
  const hire = isDraft(role) ? null : role.hire;
  if (!hire) {
    return (
      <span className="flex min-w-0 items-center gap-1">
        <ReasonChip reason={role.reason} backfillFor={role.backfillFor} />
      </span>
    );
  }
  const person = hire.person;
  /*
    The TOOLTIP carries the seat's whole story. It is the one place both sides
    are visible at once, and it is why a filled row does not need to be opened
    to be understood: what they were hired as, what they do now, and when.
  */
  const help = [
    person?.position && person.position !== role.title ? `Now: ${person.position}` : null,
    `Hired as: ${role.title}`,
    hire.startDate ? `Start: ${hire.startDate}` : null,
    hire.offerAcceptedMonth ? `Offer accepted: ${hire.offerAcceptedMonth}` : null,
    role.reason === 'backfill' && role.backfillFor ? `Backfill for ${role.backfillFor}` : null,
    hire.movedUnit ? `Hired into ${hire.movedUnit}, now in ${person?.unit}` : null,
  ]
    .filter(Boolean)
    .join('\n');
  return (
    <span className="flex min-w-0 items-center gap-1" title={help}>
      <span className="min-w-0 truncate text-[12px] text-gray-700">{person?.name || hire.name}</span>
      {hire.movedUnit && (
        <span
          className="shrink-0 rounded-sm border border-gray-200 px-1 text-[10px] leading-4 text-gray-400"
          title={`Hired into ${hire.movedUnit}, now in ${person?.unit}`}
        >
          moved
        </span>
      )}
    </span>
  );
}
